/**
 * WorkspacePanel — right-side panel that accumulates visual widgets
 * (charts, KPI cards) from chart-able assistant responses (PR 8).
 *
 * Visual layer only — the parent owns:
 *   - `widgets`: array of widget descriptors (one per pinned response)
 *   - `onRemoveWidget(id)`: drop a widget out of the panel
 *   - `onClose`: hide the panel and restore the sidebar
 *   - `onSave`: snapshot the current widgets as a saved dashboard
 *     (stub in PR 8; wired to the dashboard-save endpoint in a follow-up)
 *
 * Layout coordination (50/50 with chat when open, sidebar auto-collapsed)
 * lives in UnifiedChat — this component is content-only.
 */
import React, { useState, useRef } from "react";
import { mergeStyleSets } from "@fluentui/react";
import {
  X,
  FloppyDisk,
  ChartBar,
  PushPin,
  CaretUp,
  CaretDown,
  PencilSimple,
} from "@phosphor-icons/react";
import { boroondaraPalette } from "../../theme/boroondaraTheme";
import type { ChartData } from "../../types/agent.types";
import ChartPanel from "./ChartPanel";

/** Each widget is one visual extracted from an assistant response.
 *  Kept narrow on purpose — adding new widget kinds (map, filter, text)
 *  later means extending the `kind` union + adding a renderer. */
export interface WorkspaceWidget {
  id: string;
  /** Source message id — useful for highlighting / scroll-to in the chat. */
  messageId: string;
  /** Short label rendered in the widget head. */
  caption: string;
  kind: "chart" | "kpi";
  /** ChartData payload — same shape from the API response. */
  data: ChartData;
  /** For chart widgets: which chart type to render. */
  chartType?: "bar" | "line" | "pie";
}

interface Props {
  widgets: WorkspaceWidget[];
  onRemoveWidget: (id: string) => void;
  onClose: () => void;
  onSave?: () => void;
  /** Forwarded to chart widgets so clicking a bar / segment drills in. */
  onDrilldown?: (field: string, value: string) => void;
  /** Move a widget up/down in the stack (session-scoped). */
  onReorderWidget?: (id: string, direction: "up" | "down") => void;
  /** Rename a widget's caption (session-scoped). */
  onRenameWidget?: (id: string, caption: string) => void;
}

const styles = mergeStyleSets({
  // The frame styles are applied by the parent (.workspace container in
  // UnifiedChat); this root sits inside it and fills available space.
  root: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    background: "#FAFAFA",
    overflow: "hidden",
  },
  header: {
    flexShrink: 0,
    background: "#FFFFFF",
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  title: {
    fontSize: 13,
    fontWeight: 600,
    color: boroondaraPalette.text,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  countPill: {
    background: "#f3fcf3",
    border: "1px solid #c9eccc",
    color: boroondaraPalette.primary,
    fontSize: 10.5,
    fontWeight: 600,
    padding: "1px 7px",
    borderRadius: 999,
  },
  saveBtn: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #c9eccc",
    background: "#f3fcf3",
    color: boroondaraPalette.primary,
    fontFamily: "inherit",
    fontSize: 11.5,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.12s, border-color 0.12s",
    selectors: {
      ":hover": { background: "#d9f4d7", borderColor: boroondaraPalette.primary },
      ":disabled": { opacity: 0.55, cursor: "default" },
    },
  } as any,
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 0,
    background: "transparent",
    color: boroondaraPalette.text3,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.12s, color 0.12s",
    selectors: {
      ":hover": { background: "#F0F0F0", color: boroondaraPalette.text },
    },
  } as any,

  body: {
    flex: 1,
    overflowY: "auto",
    padding: "12px 12px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  } as any,

  widget: {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    // Critical for the body's overflow-y to actually scroll: without
    // this, the flex column tries to fit all widgets into the available
    // height by shrinking each one, never triggering overflow. Forcing
    // flex-shrink: 0 makes widgets keep their natural height and the
    // body becomes scrollable when they exceed it.
    flexShrink: 0,
  },
  widgetHead: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 12px",
    background: "#fbfbfb",
    borderBottom: "1px solid #E5E7EB",
    selectors: {
      // Surface the rename pencil only while hovering the widget head.
      ":hover .wp-pencil": { opacity: 0.5 },
    },
  } as any,
  widgetLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    fontWeight: 600,
    color: boroondaraPalette.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  } as any,
  // Click-to-rename affordance: button styled exactly like the label, with
  // a faint pencil that surfaces on widget hover.
  widgetLabelBtn: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: 0,
    background: "transparent",
    textAlign: "left" as const,
    cursor: "text",
    borderRadius: 4,
    padding: "2px 4px",
    margin: "-2px -4px",
    transition: "background 0.12s",
    selectors: {
      ":hover": { background: "#f0f1f2" },
      ":focus-visible": { outline: `2px solid ${boroondaraPalette.primary}`, outlineOffset: 0 },
    },
  } as any,
  widgetLabelText: {
    minWidth: 0,
    fontSize: 11,
    fontWeight: 600,
    color: boroondaraPalette.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  } as any,
  widgetPencil: {
    flexShrink: 0,
    color: boroondaraPalette.text3,
    opacity: 0,
    transition: "opacity 0.12s",
  } as any,
  widgetLabelInput: {
    flex: 1,
    minWidth: 0,
    font: "inherit",
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    color: boroondaraPalette.text,
    border: `1px solid ${boroondaraPalette.primary}`,
    borderRadius: 4,
    padding: "2px 6px",
    outline: "none",
    background: "#FFFFFF",
  } as any,
  widgetActions: {
    display: "flex",
    gap: 2,
    opacity: 0,
    transition: "opacity 0.12s",
  } as any,
  // .widget:hover .widgetActions trick — implemented via inline mouseEnter / mouseLeave
  widgetIconBtn: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: 0,
    background: "transparent",
    color: boroondaraPalette.text3,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    selectors: {
      ":hover": { background: "rgba(0,0,0,0.06)", color: boroondaraPalette.text },
      ":disabled": { opacity: 0.3, cursor: "default", background: "transparent", color: boroondaraPalette.text3 },
    },
  } as any,
  widgetIconBtnDanger: {
    selectors: {
      ":hover": { background: "#fde8e6", color: "#B42318" },
    },
  } as any,
  widgetBody: {
    padding: "14px 16px",
  },

  // KPI cards — same dimensions as the deployed in-chat KPI cards (PR 4a)
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
  },
  kpiCard: {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "14px 16px",
  },
  kpiLabel: {
    fontSize: 11,
    color: boroondaraPalette.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    fontWeight: 600,
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 600,
    lineHeight: "1.1",
    color: boroondaraPalette.text,
    letterSpacing: "-0.01em",
    fontVariantNumeric: "tabular-nums",
  } as any,
  kpiValueBad: { color: "#B42318" },
  kpiValueGood: { color: boroondaraPalette.primary },

  emptyState: {
    margin: "24px 6px",
    padding: "32px 16px",
    border: "1px dashed #E5E7EB",
    borderRadius: 10,
    background: "#FFFFFF",
    textAlign: "center" as const,
    color: boroondaraPalette.text3,
    fontSize: 12.5,
    lineHeight: "1.55",
    flexShrink: 0,
  } as any,
  emptyIcon: {
    color: "#9CA3AF",
    marginBottom: 8,
    display: "flex",
    justifyContent: "center",
  } as any,
  emptyTitle: { color: boroondaraPalette.text2, fontWeight: 600 },
});

/* ─── KPI value formatting (mirrors UnifiedChat.formatKpiValue) ─── */
function parseNumeric(raw: any): { num: number; isCurrency: boolean; isPercent: boolean } | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") return { num: raw, isCurrency: false, isPercent: false };
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const isPercent = trimmed.endsWith("%");
  const isCurrency = trimmed.includes("$");
  const isNegative = trimmed.startsWith("-");
  const numStr = trimmed.replace(/[$%,]/g, "").replace(/^-/, "");
  const num = parseFloat(numStr);
  if (isNaN(num)) return null;
  return { num: isNegative ? -num : num, isCurrency, isPercent };
}

function formatKpiValue(raw: any): string {
  const parsed = parseNumeric(raw);
  if (!parsed) return raw == null ? "—" : String(raw);
  const { num, isCurrency, isPercent } = parsed;
  if (isPercent) return `${num.toFixed(1)}%`;
  const abs = Math.abs(num);
  let body: string;
  if (abs >= 1_000_000_000) body = `${(abs / 1_000_000_000).toFixed(1)}B`;
  else if (abs >= 1_000_000) body = `${(abs / 1_000_000).toFixed(1)}M`;
  else if (abs >= 10_000) body = `${(abs / 1_000).toFixed(1)}k`;
  else if (abs >= 1) body = abs.toLocaleString("en-AU", { maximumFractionDigits: 0 });
  else body = abs.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sign = num < 0 ? "-" : "";
  const symbol = isCurrency ? "$" : "";
  return `${sign}${symbol}${body}`;
}

function formatHeader(name: string): string {
  let clean = name.replace(/^\[|\]$/g, "").trim();
  if (/\s/.test(clean)) return clean;
  clean = clean.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");
  clean = clean.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  clean = clean.replace(/_/g, " ");
  return clean.replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ─── Renderers per widget kind ─── */
function renderKpiCards(data: ChartData) {
  const row = data.rows?.[0];
  if (!row) return null;
  return (
    <div className={styles.kpiRow}>
      {data.fields.map((field) => {
        const raw = row[field.name];
        const parsed = parseNumeric(raw);
        const isVar = /variance|change|delta|gain|loss|diff/i.test(field.name);
        const cls = isVar && parsed && parsed.num < 0
          ? `${styles.kpiValue} ${styles.kpiValueBad}`
          : isVar && parsed && parsed.num > 0
            ? `${styles.kpiValue} ${styles.kpiValueGood}`
            : styles.kpiValue;
        return (
          <div key={field.name} className={styles.kpiCard}>
            <div className={styles.kpiLabel}>{formatHeader(field.name)}</div>
            <div className={cls}>{formatKpiValue(raw)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function WorkspacePanel({ widgets, onRemoveWidget, onClose, onSave, onDrilldown, onReorderWidget, onRenameWidget }: Props) {
  const hasWidgets = widgets.length > 0;

  // Inline rename state. `skipBlurRef` lets Escape cancel without the input's
  // blur handler re-committing the (discarded) draft.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const skipBlurRef = useRef(false);

  const startRename = (id: string, current: string) => {
    // Clear any stale skip flag from a prior Escape whose unmount-blur may
    // not have fired, so this edit's blur commits normally.
    skipBlurRef.current = false;
    setDraft(current);
    setEditingId(id);
  };
  const commitRename = (id: string) => {
    const trimmed = draft.trim();
    if (trimmed && onRenameWidget) onRenameWidget(id, trimmed);
    setEditingId(null);
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>
          <PushPin size={14} weight="fill" style={{ color: boroondaraPalette.primary }} />
          Workspace
          <span className={styles.countPill}>{widgets.length}</span>
        </span>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={onSave}
          disabled={!hasWidgets || !onSave}
          title={
            !hasWidgets
              ? "Pin widgets first"
              : onSave
                ? "Save these widgets as a dashboard"
                : "Save will be available in a follow-up"
          }
        >
          <FloppyDisk size={13} weight="regular" />
          Save as dashboard
        </button>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          title="Close workspace"
          aria-label="Close workspace"
        >
          <X size={14} weight="bold" />
        </button>
      </div>

      <div className={styles.body}>
        {hasWidgets ? (
          widgets.map((w, i) => (
            <div
              key={w.id}
              className={styles.widget}
              onMouseEnter={(e) => {
                const actions = e.currentTarget.querySelector<HTMLElement>("[data-widget-actions]");
                if (actions) actions.style.opacity = "1";
              }}
              onMouseLeave={(e) => {
                const actions = e.currentTarget.querySelector<HTMLElement>("[data-widget-actions]");
                if (actions) actions.style.opacity = "0";
              }}
            >
              <div className={styles.widgetHead}>
                {editingId === w.id ? (
                  <input
                    className={styles.widgetLabelInput}
                    value={draft}
                    autoFocus
                    onChange={(e) => setDraft(e.target.value)}
                    onFocus={(e) => e.currentTarget.select()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitRename(w.id);
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        skipBlurRef.current = true;
                        setEditingId(null);
                      }
                    }}
                    onBlur={() => {
                      if (skipBlurRef.current) { skipBlurRef.current = false; return; }
                      commitRename(w.id);
                    }}
                    aria-label="Rename widget"
                  />
                ) : onRenameWidget ? (
                  <button
                    type="button"
                    className={styles.widgetLabelBtn}
                    title="Click to rename"
                    onClick={() => startRename(w.id, w.caption)}
                  >
                    <span className={styles.widgetLabelText}>{w.caption}</span>
                    <PencilSimple size={10} weight="regular" className={`${styles.widgetPencil} wp-pencil`} />
                  </button>
                ) : (
                  <span className={styles.widgetLabel} title={w.caption}>{w.caption}</span>
                )}
                <div data-widget-actions className={styles.widgetActions}>
                  {onReorderWidget && widgets.length > 1 && (
                    <>
                      <button
                        type="button"
                        className={styles.widgetIconBtn}
                        onClick={() => onReorderWidget(w.id, "up")}
                        disabled={i === 0}
                        title="Move up"
                        aria-label="Move widget up"
                      >
                        <CaretUp size={12} weight="bold" />
                      </button>
                      <button
                        type="button"
                        className={styles.widgetIconBtn}
                        onClick={() => onReorderWidget(w.id, "down")}
                        disabled={i === widgets.length - 1}
                        title="Move down"
                        aria-label="Move widget down"
                      >
                        <CaretDown size={12} weight="bold" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className={`${styles.widgetIconBtn} ${styles.widgetIconBtnDanger}`}
                    onClick={() => onRemoveWidget(w.id)}
                    title="Remove from workspace"
                    aria-label="Remove widget"
                  >
                    <X size={12} weight="bold" />
                  </button>
                </div>
              </div>
              <div className={styles.widgetBody}>
                {w.kind === "chart" && w.chartType ? (
                  <ChartPanel
                    data={w.data}
                    chartType={w.chartType}
                    embedded
                    height={300}
                    onDrilldown={onDrilldown}
                  />
                ) : w.kind === "kpi" ? (
                  renderKpiCards(w.data)
                ) : null}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <ChartBar size={28} weight="regular" />
            </div>
            <div className={styles.emptyTitle}>Visuals from your chat appear here.</div>
            <div style={{ marginTop: 4 }}>
              Each chartable answer adds a widget automatically — chat keeps the table, the chart and headline figures live here.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
